class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', this.handleSaveForLaterClick.bind(this));
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('click', this.handleOutsideClick.bind(this));
    document.addEventListener('skipp:city-changed', () => this.filterRecommendationCards());
    document.addEventListener('locationUpdated', () => this.filterRecommendationCards());
    this.setHeaderCartIconAccessibility();
    setTimeout(() => this.positionRecommendationsAfterItems());
    this.addEventListener('change', (event) => {
      if (event.target instanceof HTMLSelectElement && event.target.classList.contains('variant-dropdown')) {
        this.fitVariantDropdowns();
      }
    });
  }

  handleOutsideClick(event) {
    if (!this.classList.contains('active')) return;

    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && event.target instanceof Node && drawerInner.contains(event.target)) return;

    this.close();
  }

  handleSaveForLaterClick(event) {
    const recommendationButton = event.target.closest('[data-cart-recommendation-add]');
    if (recommendationButton) {
      this.handleRecommendationAddClick(event, recommendationButton);
      return;
    }

    /* Save for later — handled by skipp-cart-save-wishlist.js */
  }

  handleRecommendationAddClick(event, button) {
    event.preventDefault();
    const variantId = button.dataset.variantId;
    if (!variantId || button.getAttribute('aria-disabled') === 'true') return;

    const feedback = button
      .closest('[data-cart-recommendation-card]')
      ?.querySelector('[data-cart-recommendation-feedback]');

    button.setAttribute('aria-disabled', 'true');
    button.classList.add('is-loading');

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    formData.append(
      'sections',
      this.getSectionsToRender().map((section) => section.id)
    );
    formData.append('sections_url', window.location.pathname);
    this.setActiveElement(button);

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          button.removeAttribute('aria-disabled');
          button.classList.remove('is-loading');
          return;
        }

        if (feedback) {
          feedback.hidden = false;
        }
        button.textContent = 'Added';

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer-recommendations',
          productVariantId: variantId,
          cartData: response,
        });

        CartPerformance.measure('recommendation:add:paint-updated-sections', () => {
          this.renderContents(response);
        });
      })
      .catch((error) => {
        button.removeAttribute('aria-disabled');
        button.classList.remove('is-loading');
      });
  }

  addToWishlist(productData) {
    const storageKey = 'onhow-wishlist';
    let wishlist = [];
    try {
      wishlist = JSON.parse(localStorage.getItem(storageKey) || '[]');
      if (!Array.isArray(wishlist)) wishlist = [];
    } catch (error) {
      wishlist = [];
    }
    const exists = wishlist.some(
      (item) =>
        (productData.id && item.id && String(item.id) === String(productData.id)) ||
        (item.title === productData.title && item.url === productData.url)
    );

    if (!exists) {
      wishlist.push(productData);
      localStorage.setItem(storageKey, JSON.stringify(wishlist));
      document.dispatchEvent(new CustomEvent('skipp:wishlist-updated'));
    }

    this.updateWishlistCount(wishlist.length);
  }

  updateWishlistCount(count) {
    document.querySelectorAll('.onhow-wishlist-count').forEach((el) => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });

    if (window.onhowWishlist && typeof window.onhowWishlist.updateWishlistCount === 'function') {
      window.onhowWishlist.updateWishlistCount();
    }
  }

  setHeaderCartIconAccessibility() {
    const bindCartTrigger = (cartLink) => {
      if (!cartLink || cartLink.dataset.skippCartDrawerBound === '1') return;
      cartLink.dataset.skippCartDrawerBound = '1';
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    };

    bindCartTrigger(document.querySelector('#cart-icon-bubble'));
    bindCartTrigger(document.querySelector('#cart-icon-bubble-mobile'));
    bindCartTrigger(document.querySelector('#cart-icon-bubble-app'));
    bindCartTrigger(document.querySelector('.mobile-bottom-nav__bag'));
  }

  static isMobileCartViewport() {
    try {
      return window.matchMedia('(max-width: 768px)').matches;
    } catch (_e) {
      return false;
    }
  }

  static handleMobileCartPageAsDrawer() {
    if (!document.body.classList.contains('template-cart')) return;
    if (!CartDrawer.isMobileCartViewport()) return;

    document.body.classList.add('skipp-mweb-cart-drawer-mode');

    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') return;

    const exitCartPage = () => {
      if (!document.body.classList.contains('template-cart')) return;
      if (window.history.length > 1) {
        window.history.back();
        return;
      }
      window.location.href = window.routes?.root_url || '/';
    };

    document.addEventListener(
      'skipp:cart-drawer-closed',
      () => {
        if (document.body.classList.contains('skipp-mweb-cart-drawer-mode')) {
          exitCartPage();
        }
      },
      { once: true }
    );

    requestAnimationFrame(() => {
      drawer.open();
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.filterRecommendationCards();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__close') || this.querySelector('.drawer__inner');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    document.body.classList.add('cart-drawer-open');
    document.dispatchEvent(new CustomEvent('skipp:cart-drawer-opened'));
    requestAnimationFrame(() => this.fitVariantDropdowns());
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.body.classList.remove('cart-drawer-open');
    document.dispatchEvent(new CustomEvent('skipp:cart-drawer-closed'));
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  /** Cart line count from cart JSON (/cart.js, /cart/add.js, etc.). */
  static resolveCartItemCount(data) {
    if (!data) return 0;
    const cart = data.cart && typeof data.cart === 'object' ? data.cart : data;
    const raw = cart.item_count ?? cart.itemCount ?? data.item_count ?? data.itemCount;
    if (raw != null && raw !== '') {
      const n = Number(raw);
      if (Number.isFinite(n)) return Math.max(0, Math.floor(n));
    }
    const items = Array.isArray(cart.items) ? cart.items : Array.isArray(data.items) ? data.items : null;
    if (items && items.length) {
      return items.reduce((sum, line) => sum + (Number(line.quantity) || 0), 0);
    }
    return 0;
  }

  static cartJsonUrl() {
    const base = window.routes && window.routes.cart_url ? String(window.routes.cart_url) : '/cart';
    const trimmed = base.replace(/\/$/, '');
    return trimmed.endsWith('.js') ? trimmed : `${trimmed}.js`;
  }

  /**
   * Header cart links use custom icons (Skipp desktop) / structure; only update numeric badges.
   */
  applyCartBubbleCounts(count) {
    const n = Number.isFinite(Number(count)) ? Math.max(0, Math.floor(Number(count))) : 0;

    const desktop = document.getElementById('cart-icon-bubble');
    if (desktop) {
      let badge = desktop.querySelector('.modern-header__cart-count');
      if (n > 0) {
        if (!badge) {
          badge = document.createElement('div');
          badge.className = 'modern-header__cart-count';
          const span = document.createElement('span');
          span.setAttribute('aria-hidden', 'true');
          badge.appendChild(span);
          desktop.appendChild(badge);
        }
        const num = badge.querySelector('span[aria-hidden="true"]') || badge.querySelector('span');
        if (num && n < 100) num.textContent = String(n);
        badge.style.display = 'flex';
      } else if (badge) {
        badge.style.display = 'none';
      }
    }

    const mobile = document.getElementById('cart-icon-bubble-mobile');
    if (mobile) {
      let badge = mobile.querySelector('.cart-count-bubble');
      if (n > 0) {
        if (!badge) {
          badge = document.createElement('div');
          badge.className = 'cart-count-bubble';
          const shown = document.createElement('span');
          shown.setAttribute('aria-hidden', 'true');
          badge.appendChild(shown);
          const hidden = document.createElement('span');
          hidden.className = 'visually-hidden';
          badge.appendChild(hidden);
          mobile.appendChild(badge);
        }
        const shown = badge.querySelector('span[aria-hidden="true"]');
        if (shown && n < 100) shown.textContent = String(n);
        const hidden = badge.querySelector('.visually-hidden');
        if (hidden) hidden.textContent = `${n} items`;
        badge.style.removeProperty('display');
      } else if (badge) {
        badge.style.display = 'none';
      }
    }
  }

  /**
   * Paint immediately from add-to-cart payload, then reconcile from /cart.js (authoritative).
   */
  updateHeaderCartBubbleCounts(parsedState) {
    this.applyCartBubbleCounts(CartDrawer.resolveCartItemCount(parsedState));
    fetch(CartDrawer.cartJsonUrl(), { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        if (cart) this.applyCartBubbleCounts(CartDrawer.resolveCartItemCount(cart));
      })
      .catch(() => {});
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;

    const drawerItems = document.querySelector('cart-drawer-items');
    if (drawerItems && typeof drawerItems.applyDrawerCartSections === 'function') {
      drawerItems.applyDrawerCartSections(parsedState);
    } else {
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return;
        const sectionKey = section.section || section.id;
        const html = parsedState.sections && parsedState.sections[sectionKey];
        if (!html) return;
        sectionElement.innerHTML = this.getSectionInnerHTML(html, section.selector);
      });
      CartDrawerItems.dispatchDrawerCartUpdated(parsedState);
    }

    this.updateHeaderCartBubbleCounts(parsedState);

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.filterRecommendationCards();
      this.open();
    });
  }

  filterRecommendationCards() {
    this.positionRecommendationsAfterItems();
  }

  positionRecommendationsAfterItems() {
    const recommendations = this.querySelector('[data-cart-recommendations]');
    const itemsWrapper = this.querySelector('.drawer__cart-items-wrapper');
    const cartTable = itemsWrapper?.querySelector('.cart-items');

    if (recommendations && itemsWrapper && cartTable && recommendations.previousElementSibling !== cartTable) {
      cartTable.insertAdjacentElement('afterend', recommendations);
    }

    this.fitVariantDropdowns();
  }

  fitVariantDropdowns(root = this) {
    if (!root) return;
    root.querySelectorAll('skipp-cart-variant').forEach((picker) => {
      if (typeof picker.syncFromSelect === 'function') picker.syncFromSelect();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  onChange(event) {
    if (event.target instanceof HTMLSelectElement && event.target.classList.contains('variant-dropdown')) {
      this.onDrawerVariantChange(event);
      return;
    }
    super.onChange(event);
  }

  onDrawerVariantChange(event) {
    const select = event.target;
    const newId = String(select.value);
    const prevId = String(select.dataset.initialVariantId || '');
    if (!prevId || newId === prevId) return;

    const lineKey = select.dataset.lineKey;
    const line = select.dataset.lineIndex;
    if (!lineKey || !line) {
      select.value = prevId;
      return;
    }

    const qtyEl = this.querySelector(`#Drawer-quantity-${line}`);
    let qty = parseInt(qtyEl ? qtyEl.value : '', 10);
    if (!Number.isFinite(qty) || qty < 1) qty = 1;

    const sections = this.getSectionsToRender().map((s) => s.section);
    const sections_url = window.location.pathname;

    this.enableLoading(line);
    select.disabled = true;

    fetch(`${routes.cart_change_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({
        id: lineKey,
        quantity: 0,
        sections,
        sections_url,
      }),
    })
      .then((res) => res.text())
      .then((text) => {
        const data = JSON.parse(text);
        if (data.errors) {
          const msg = typeof data.errors === 'string' ? data.errors : JSON.stringify(data.errors);
          throw new Error(msg);
        }
        return fetch(`${routes.cart_add_url}`, {
          ...fetchConfig(),
          body: JSON.stringify({
            items: [{ id: Number(newId), quantity: qty }],
            sections,
            sections_url,
          }),
        });
      })
      .then((res) => res.text())
      .then((text) => {
        const data = JSON.parse(text);
        if (data.errors) {
          throw new Error(data.errors);
        }
        return this.refreshDrawerFromServer();
      })
      .then(() => fetch('/cart.js'))
      .then((r) => r.json())
      .then((cart) => {
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cartData: cart } }));
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cart, variantId: newId });
      })
      .catch((err) => {
        select.value = prevId;
        this.refreshDrawerFromServer().catch(() => window.location.reload());
      })
      .finally(() => {
        select.disabled = false;
        this.disableLoading(line);
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.fitVariantDropdowns === 'function') {
          cartDrawer.fitVariantDropdowns();
        }
      });
  }

  getSectionsToRender() {
    const sections = [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
    if (document.getElementById('cart-icon-bubble-mobile')) {
      sections.push({
        id: 'cart-icon-bubble-mobile',
        section: 'cart-icon-bubble-mobile',
        selector: '.shopify-section',
      });
    }
    return sections;
  }

  applyDrawerCartSections(parsedState) {
    const cartData = parsedState.cart || parsedState;

    if (!cartData || cartData.item_count === 0) {
      this.refreshDrawerFromServer().then(() => {
        CartDrawerItems.dispatchDrawerCartUpdated(cartData);
      });
      return;
    }

    const sectionHtml = parsedState.sections?.['cart-drawer'];
    if (!sectionHtml) {
      this.refreshDrawerFromServer().then(() => {
        CartDrawerItems.dispatchDrawerCartUpdated(cartData);
      });
      return;
    }

    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const newItems = doc.querySelector('cart-drawer-items');
    const currentItems = document.querySelector('cart-drawer-items');
    if (newItems && currentItems) {
      currentItems.replaceWith(newItems);
    }

    const newFooter = doc.querySelector('.cart-drawer__footer');
    const currentFooter = document.querySelector('.cart-drawer__footer');
    if (newFooter && currentFooter) {
      currentFooter.replaceWith(newFooter);
    }

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.classList.toggle('is-empty', cartData.item_count === 0);
      if (typeof cartDrawer.fitVariantDropdowns === 'function') {
        cartDrawer.fitVariantDropdowns();
      }
    }

    CartDrawerItems.dispatchDrawerCartUpdated(cartData);
  }

  async refreshDrawerFromServer() {
    const url = `${window.routes?.cart_url || '/cart'}?section_id=cart-drawer`;
    const response = await fetch(url);
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const sectionRoot = doc.querySelector('[id^="shopify-section"]') || doc;

    const newCartPanel = sectionRoot.querySelector('#cart-drawer-cart-panel');
    const currentCartPanel = document.querySelector('#cart-drawer-cart-panel');
    if (newCartPanel && currentCartPanel) {
      currentCartPanel.replaceWith(newCartPanel);
    } else {
      const newItems = sectionRoot.querySelector('cart-drawer-items');
      const currentItems = document.querySelector('cart-drawer-items');
      if (newItems && currentItems) {
        currentItems.replaceWith(newItems);
      }

      const newFooter = sectionRoot.querySelector('.cart-drawer__footer');
      const currentFooter = document.querySelector('.cart-drawer__footer');
      if (newFooter && currentFooter) {
        currentFooter.replaceWith(newFooter);
      }
    }

    const newWishlistBack = sectionRoot.querySelector(
      '#cart-drawer-wishlist-content .cart-wishlist-back'
    );
    const currentWishlistBack = document.querySelector(
      '#cart-drawer-wishlist-content .cart-wishlist-back'
    );
    if (newWishlistBack && currentWishlistBack) {
      currentWishlistBack.replaceWith(newWishlistBack.cloneNode(true));
    }

    const newCartDrawer = sectionRoot.querySelector('cart-drawer');
    const currentCartDrawer = document.querySelector('cart-drawer');
    if (newCartDrawer?.dataset?.wishlistBackIcon && currentCartDrawer) {
      currentCartDrawer.dataset.wishlistBackIcon = newCartDrawer.dataset.wishlistBackIcon;
    }

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.classList.toggle('is-empty', !doc.querySelector('.cart-item'));
      if (typeof cartDrawer.fitVariantDropdowns === 'function') {
        cartDrawer.fitVariantDropdowns();
      }
      if (typeof cartDrawer.positionRecommendationsAfterItems === 'function') {
        cartDrawer.positionRecommendationsAfterItems();
      }
    }

    CartDrawerItems.syncWishlistBackIcon();
    document.dispatchEvent(new CustomEvent('skipp:cart-ui-refreshed'));
  }

  static syncWishlistBackIcon() {
    /* Icon is inline SVG in skipp-cart-wishlist-back-button.liquid — no runtime image swap. */
  }

  static dispatchDrawerCartUpdated(cartData) {
    window.requestAnimationFrame(() => {
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: { cartData, source: 'cart-drawer-items' },
        })
      );
      document.dispatchEvent(new CustomEvent('skipp:cart-ui-refreshed'));
    });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class SkippCartVariant extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this.select = this.querySelector('select.variant-dropdown');
    this.trigger = this.querySelector('.skipp-cart-variant__trigger');
    this.valueEl = this.querySelector('.skipp-cart-variant__value');
    this.menu = this.querySelector('.skipp-cart-variant__menu');
    if (!this.select || !this.trigger || !this.menu) return;
    this._bound = true;

    this._onTrigger = this.toggle.bind(this);
    this._onDocClick = this.onDocumentClick.bind(this);
    this._onKey = this.onKeydown.bind(this);
    this._onReposition = this.placeMenu.bind(this);
    this.trigger.addEventListener('click', this._onTrigger);
    this.menu.addEventListener('click', (event) => {
      const option = event.target.closest('[data-variant-id]');
      if (!option || option.disabled) return;
      this.choose(option.dataset.variantId, option);
    });
    this.syncFromSelect();
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this._onTrigger);
    window.clearTimeout(this._docTimer);
    document.removeEventListener('pointerdown', this._onDocClick, true);
    document.removeEventListener('keydown', this._onKey);
    window.removeEventListener('resize', this._onReposition);
    window.removeEventListener('scroll', this._onReposition, true);
    if (this.menu && this.menu.parentElement === document.body) {
      this.appendChild(this.menu);
    }
    if (this.menu) {
      this.menu.hidden = true;
      this.menu.classList.remove('is-open');
      this.menu.style.display = '';
    }
    this.classList.remove('is-open');
    this._bound = false;
  }

  syncFromSelect() {
    if (!this.select || !this.valueEl) return;
    const selected = this.select.options[this.select.selectedIndex];
    if (selected) this.valueEl.textContent = selected.textContent.trim();
    this.menu?.querySelectorAll('[data-variant-id]').forEach((btn) => {
      const on = String(btn.dataset.variantId) === String(this.select.value);
      btn.classList.toggle('is-selected', on);
      btn.setAttribute('aria-selected', on ? 'true' : 'false');
    });
  }

  toggle(event) {
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    if (this.classList.contains('is-open')) this.close();
    else this.open();
  }

  open() {
    document.querySelectorAll('skipp-cart-variant.is-open').forEach((el) => {
      if (el !== this) el.close();
    });
    this.menu.hidden = false;
    this.menu.removeAttribute('hidden');
    this.menu.classList.add('is-open');
    this.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.placeMenu();
    window.clearTimeout(this._docTimer);
    this._docTimer = window.setTimeout(() => {
      document.addEventListener('pointerdown', this._onDocClick, true);
      document.addEventListener('keydown', this._onKey);
      window.addEventListener('resize', this._onReposition);
      window.addEventListener('scroll', this._onReposition, true);
    }, 0);
  }

  placeMenu() {
    if (!this.menu || !this.trigger) return;
    document.body.appendChild(this.menu);
    const triggerRect = this.trigger.getBoundingClientRect();
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const minWidth = Math.max(triggerRect.width, 168);
    const left = Math.min(Math.max(8, triggerRect.left), Math.max(8, vw - minWidth - 8));
    this.menu.style.position = 'fixed';
    this.menu.style.top = `${Math.round(triggerRect.bottom + 6)}px`;
    this.menu.style.left = `${Math.round(left)}px`;
    this.menu.style.minWidth = `${Math.round(minWidth)}px`;
    this.menu.style.maxWidth = `${Math.min(280, vw - 16)}px`;
    this.menu.style.zIndex = '2147483647';
    this.menu.style.display = 'block';
    this.menu.style.pointerEvents = 'auto';
    this.menu.style.maxHeight = 'min(280px, calc(100vh - 24px))';
    this.menu.style.overflowY = 'auto';
  }

  close() {
    window.clearTimeout(this._docTimer);
    document.removeEventListener('pointerdown', this._onDocClick, true);
    document.removeEventListener('keydown', this._onKey);
    window.removeEventListener('resize', this._onReposition);
    window.removeEventListener('scroll', this._onReposition, true);
    if (!this.menu) return;
    this.menu.hidden = true;
    this.menu.classList.remove('is-open');
    this.menu.style.display = '';
    this.menu.style.top = '';
    this.menu.style.left = '';
    this.classList.remove('is-open');
    this.trigger?.setAttribute('aria-expanded', 'false');
    if (this.menu.parentElement !== this) {
      this.appendChild(this.menu);
    }
  }

  onDocumentClick(event) {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    if (path.includes(this) || path.includes(this.menu) || this.contains(event.target) || this.menu.contains(event.target)) {
      return;
    }
    this.close();
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.close();
  }

  choose(variantId, optionBtn) {
    if (!variantId || String(this.select.value) === String(variantId)) {
      this.close();
      return;
    }
    this.select.value = variantId;
    if (this.valueEl) {
      this.valueEl.textContent =
        optionBtn?.querySelector('span')?.textContent.trim() || optionBtn?.textContent.trim() || '';
    }
    this.syncFromSelect();
    this.close();
    this.select.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('skipp-cart-variant')) {
  customElements.define('skipp-cart-variant', SkippCartVariant);
}

if (typeof window !== 'undefined') {
  window.SkippCartDrawerUi = window.SkippCartDrawerUi || {};
  window.SkippCartDrawerUi.syncWishlistBackIcon = CartDrawerItems.syncWishlistBackIcon.bind(
    CartDrawerItems
  );
  const runWishlistBackIconSync = () => CartDrawerItems.syncWishlistBackIcon();

  document.addEventListener('DOMContentLoaded', runWishlistBackIconSync);
  document.addEventListener('shopify:section:load', runWishlistBackIconSync);
  document.addEventListener('skipp:cart-ui-refreshed', runWishlistBackIconSync);

  const bootMobileCartDrawer = () => {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.setHeaderCartIconAccessibility === 'function') {
      drawer.setHeaderCartIconAccessibility();
    }
    CartDrawer.handleMobileCartPageAsDrawer();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootMobileCartDrawer);
  } else {
    bootMobileCartDrawer();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.setHeaderCartIconAccessibility !== 'function') return;
    if (event.target && event.target.querySelector && event.target.querySelector('.mobile-bottom-nav__bag')) {
      drawer.setHeaderCartIconAccessibility();
    }
  });
}
